import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState, lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ActionCableContext } from '../startup/ConfirmShowApp';

import { ToastContainer } from 'react-toastify';
import Popup from "reactjs-popup";
import PageLayoutSelector from "../../shared/PageLayoutSelector/PageLayoutSelector";
import CheckoutEmbedModal from "../../shared/CheckoutEmbedModal/CheckoutEmbedModal";

const SectionErrorFallback = ({ error, resetErrorBoundary }) => (
  <div className="card-body">
    <div className="row">
      <div className="col-12">
        <strong className="text-red">Something went wrong</strong>
        <p className="mb-2 small text-muted">{error.message}</p>
      </div>
    </div>
  </div>
);

import {offsetDateForServer} from '../../../shared/timeZoneLogic';

import ConfirmActionMenu from './ConfirmActionMenu';
import HoldActionMenu from './HoldActionMenu';
import ManageLineup from './ManageLineup';
import FolderViewApp from '../../FolderView/startup/FolderViewApp';
import Notes from '../../Notes/components/Notes';
import ManageShowActivities from './ManageShowActivities';
import ManageContacts from './ManageContacts';
import ManageTickets from './ManageTickets';
import ManageOpendateTickets from './ManageOpendateTickets';
import ManageOffers from './ManageOffers';
import ManageSettlements from './ManageSettlements';
import MarketingSection from './MarketingSection';
import ProfitAndLoss from './ProfitAndLoss';
import DayOfShowBar from '../../EventPage/components/dashboard/DayOfShowBar';
import ShowAdvanceSendPopup from './ShowAdvanceSendPopup';
import ShareSettingsPopup from './ShareSettingsPopup';
import SetPermissionsPopup from "../../SetPermissionsPopup/components/SetPermissionsPopup";
import PageSectionMenu from './PageSectionMenu';
import ManageFinanceItems from './ManageFinanceItems';
import EventDetailsApp from '../../EventDetails/startup/EventDetailsApp';
import { EventDemandProvider, DemandHotPill, useEventDemand } from '../../EventDetails/context/EventDemandContext';
import TagManagerApp from '../../TagManager/startup/TagManagerApp';
import EventForm from '../../EventForm/components/EventForm';
import ManageRentalInvoices from './ManageRentalInvoices';
import MakeRecurringModal from './MakeRecurringModal';
import EventSeriesBanner from './EventSeriesBanner';
import CopyLinks, { canSeeAnyLink } from './CopyLinks';
import ClassicSectionCard from './ClassicSectionCard';
import ClassicSectionNav from './ClassicSectionNav';
import usePastEventDashboard from './usePastEventDashboard';
import {
  SalesPaceCard, SettlementCard, ShowRecapCard, TicketTypesCard
} from '../../EventPage/components/dashboard/PastEventDashboard';
import LastEditIndicator from '../../EventPage/components/LastEditIndicator';
import EventHeader, { EventStatusBadge } from '../../EventPage/components/EventHeader';
import classicHeaderMeta from './classicHeaderMeta';
import {
  Badge, Button, Edit01
} from '@opendate-app/setlist';
import '../styles/classicPage.css';
import { richTextToHtml } from '../../shared/utils/richTextHtml';

const axios = require('axios').default;

const newContentStyle = (browser) => {
  var styles = {
    "background": "var(--sl-bg-primary)",
    "border": "none",
    "borderRadius": "8px",
    "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
    "width": "594px",
    "maxWidth": ((browser.is.small || browser.is.extraSmall) ? "100vw" : "594px"),
    "overflow": "hidden",
    "zIndex": '200'
  }

  if (browser.greaterThan.small){
    styles["minWidth"] = "530px";
  }

  return styles;
}

const SECTIONS = [
  { key: "lineup", label: "Lineup", pageSection: "lineup" },
  { key: "schedule", label: "Show Details", pageSection: "show_details" },
  { key: "invoices", label: "Rental Invoices", pageSection: "rental_invoices" },
  { key: "offers", label: "Offers", pageSection: "offers" },
  { key: "settlements", label: "Settlements", pageSection: "settlements" },
  { key: "finances", label: "Finances", pageSection: "finance_items" },
  { key: "notes", label: "Notes", pageSection: "notes" },
  { key: "marketing", label: "Marketing", pageSection: "marketing" },
  { key: "assets", label: "Assets", pageSection: "assets" },
  { key: "tickets", label: "Tickets", pageSection: "tickets" },
  { key: "pl", label: "P&L", pageSection: "profit_loss" },
  { key: "links", label: "Links", pageSection: "links" },
  { key: "contacts", label: "Contacts", pageSection: "contacts" },
];

// Settling V2 offers, excluding those migrated to V3 so only the V3 offer settles.
const offersWithSettlements = (offers, v3Offers = []) => {
  const migratedV2Ids = new Set((v3Offers || []).map((o) => o.v2_offer_id).filter(Boolean));
  return offers.filter((o) => o.ui_version === 2 && !migratedV2Ids.has(o.id));
}

const getConfirmPageSection = (confirmPageSections, identifier) => {
  return confirmPageSections.find((cps) => cps.identifier === identifier);
}

// Wrapper component to allow future lazy-loading of heavy sections like ProfitAndLoss
// (keeps current behavior while giving us a seam for code-splitting later)
const ConfirmShowBody = ({
  browser,
  confirm,
  confirmChanged,
  refreshConfirm,
  performers,
  deletePerformer,
  csrfToken,
  team,
  teamMembershipId,
  user,
  stagePerformerForUpdate,
  updatePerformer,
  updatePerformerActType,
  performerStartTimeChanged,
  performerEndTimeChanged,
  addNewPerformer,
  setArtistOnNewPerformer,
  createPerformer,
  cancelAddPerformer,
  originalPerformer,
  cancelEditPerformer,
  currentUser,
  notes,
  noteTemplates,
  calendarEventContacts,
  createCalendarEventContactFromContactId,
  deleteCalendarEventContact,
  addCalendarEventContact,
  removeUnsavedCalendarEventContact,
  editCalendarEventContact,
  cancelEditCalendarEventContact,
  updateCalendarEventContactName,
  updateCalendarEventContactEmail,
  updateCalendarEventContactPhone,
  updateCalendarEventContactContactType,
  updateCalendarEventContactTitle,
  updateCalendarEventContactCompanyName,
  submitCalendarEventContactForm,
  editUnsavedCalendarEventContact,
  originalCalendarEventContact,
  createCalendarEventContactFromAgentId,
  modalOffer,
  defaultOffer,
  offerModalIsOpen,
  closeOfferModal,
  openOfferModal,
  offerTemplates,
  venuesOptions,
  onManageLineupInit,
  fetchOfferData,
  refreshPerformer,
  offerPerformer,
  setOfferPerformer,
  acceptOffer,
  declineOffer,
  offers,
  offerV3,
  v3Offers,
  refreshConfirmOffers,
  refreshConfirmV3Offers,
  deleteOffer,
  folder,
  updateConfirmWebsite,
  updateConfirmWebsiteRedirect,
  updateConfirmTicketForecast,
  deleteConfirm,
  cancelConfirm,
  sendingCustomerIOEmail,
  sendingCustomerIOEmailChanged,
  customerIOEmailPurpose,
  customerIOEmailPurposeChanged,
  customerIOEmailTemplate,
  customerIOEmailTemplateChanged,
  createCustomerIOEmail,
  marketingSectionActiveTab,
  marketingSectionActiveTabChanged,
  authorizedPages,
  addingNewSocialAccount,
  addingNewSocialAccountChanged,
  onepostPublicKey,
  twitterProviderId,
  facebookProviderId,
  composingPost,
  composingPostChanged,
  createPost,
  posts,
  deletePost,
  postToEdit,
  postToEditChanged,
  updatePost,
  updateCalendarEventContact,
  sendCalendarEventContactTicketCounts,
  fetchConfirmShowActivities,
  showActivities,
  deleteShowActivity,
  editShowActivity,
  cancelEditShowActivity,
  addNewShowActivity,
  showActivityNameChanged,
  showActivityStartDateChanged,
  showActivityStartTimeChanged,
  showActivityCopy,
  createShowActivity,
  updateShowActivity,
  showAdvanceSend,
  showAdvanceSendChanged,
  showAdvanceSendToChanged,
  showAdvanceSendSubjectChanged,
  showAdvanceSendBodyChanged,
  createShowAdvanceSend,
  showAdvanceSendNoteClicked,
  toggleHideLineupFromAdvance,
  toggleHideShowDetailsFromAdvance,
  venueSelected,
  confirmInvitation,
  confirmInvitationChanged,
  confirmInvitationMemoChanged,
  emailsToInvite,
  emailsToInviteChanged,
  guestRole,
  createConfirmInvitations,
  pendingConfirmInvitations,
  guestTeamMemberships,
  isViewingGuests,
  isViewingGuestsChanged,
  deleteConfirmInvitation,
  removeConfirmAccess,
  shareSettingsPopupClosed,
  selfRegisterLink,
  offerForSettlement,
  offerForSettlementChanged,
  permissionSet,
  permissionSetChanged,
  fetchPermissionSet,
  permissionRuleEnabledClicked,
  selectAllPermissionRulesClicked,
  submitPermissionSet,
  onPermissionSetSaved,
  confirmPageSections,
  updateConfirmPageSection,
  notesReplaced,
  promotersWithPermission,
  showAdvanceSends,
  socialMarketingBudgetItems,
  traditionalMarketingBudgetItems,
  metaAdSpend,
  metaAdsConnected,
  addSocialMarketingBudgetItem,
  addTraditionalMarketingBudgetItem,
  createMarketingBudgetItem,
  destroyMarketingBudgetItem,
  editMarketingBudgetItem,
  updateMarketingBudgetItem,
  marketingBudgetItemToEdit,
  marketingBudgetItemToEditChanged,
  financeItems,
  financeItemsChanged,
  financeCategories,
  financeCategoriesChanged,
  fetchNewFinanceItem,
  deleteFinanceItem,
  financeItemToEdit,
  financeItemToEditChanged,
  financeItemToEditCanceled,
  financeItemForForm,
  financeItemForFormChanged,
  createFinanceItem,
  updateFinanceItem,
  ticketTypesByPrice,
  showWidgetCode,
  showWidgetCodeChanged,
  addOnsByPrice,
  uncancelConfirm,
  postponeConfirm,
  unpostponeConfirm,
  buyers,
  buyersChanged,
  selectedBuyer,
  selectedBuyerChanged,
  promoters,
  promotersChanged,
  selectedPromoter,
  selectedPromoterChanged,
  venueOwnerships,
  editingConfirmWebsite,
  editingConfirmWebsiteChanged,
  editingConfirmWebsiteValue,
  editingConfirmWebsiteValueChanged,
  fetchOfferTotalDue,
  offerChanged,
  updateOfferArtist,
  folderChanged,
  teamRootFolder,
  profitAndLoss,
  fetchProfitAndLoss,
  refreshV3Deposits,
  updateDeposit,
  dayOfShowBar,
  mayCancelConfirm,
  mayUncancelConfirm,
  mayUnpastConfirm,
  unpastTimeZone,
  unpastBlockers,
  postEventSideEffects,
  holdGroupDeletion,
  holdGroupDeletionChanged,
  deleteHoldGroup,
  holdToConfirm,
  holdToConfirmChanged,
  applyTimeDefaults,
  doorsTimeDefault,
  eventEndTimeDefault,
  eventTimeDefaults,
  userChangedDoorsTime,
  userChangedDoorsTimeChanged,
  userChangedEventEndTime,
  userChangedEventEndTimeChanged,
  convertHoldGroupToConfirm,
  isConvertingHoldToConfirm,
  possibleMatchingOffers,
  fetchPossibleMatchingOffers,
  dismissMatchingOffers,
  acceptMatchingOffer,
  manualTicketCounts,
  railsEnv,
  showUpgradeLink,
  showDonationLink,
  calendarEventNoteEditorState,
  venuePreSalePromoCode,
  rentalInvoices,
  rentalDeposits,
  v3Deposits,
  updateV3Deposit,
  rooms,
  acceptV3Offer,
  declineV3Offer,
  deleteV3Offer,
  v3OfferChanged,
  isGuest
}) => {
  const [confirmToDup, setConfirmToDup] = useState(null)
  const [userChangedDupDoorTime, setUserChangedDupDoorTime] = useState(false)
  const [userChangedDupEndTime, setUserChangedDupEndTime] = useState(false)
  const [submittingDuplicationForm, setSubmittingDuplicationForm] = useState(false)
  const [makeRecurringOpen, setMakeRecurringOpen] = useState(false)

  const { setDemand } = useEventDemand();
  const cable = useContext(ActionCableContext)?.cable;

  useEffect(() => {
    if (!cable || !confirm?.id) return;

    const subscription = cable.subscriptions.create(
      { channel: 'InternalConfirmChannel', id: confirm.id },
      {
        received: (message) => {
          if (message?.demand_status !== undefined) {
            setDemand({
              demandStatus: message.demand_status,
              demandMultiplier: message.demand_multiplier ?? null
            });
          }
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [cable, confirm?.id, setDemand]);

  const submitDuplicationForm = (
    csrfToken,
    team,
    onClose,
    calendarEventType,
    calendarEventArtists,
    calendarEventTitle,
    venueSelected,
    calendarEventTicketForecast,
    calendarEventNoteEditorState,
    confirmInForm,
    holdGroupCopy,
    onSuccess,
    selectedPromoter,
    selectedBuyer,
    canonicalTagIds,
    selectedRooms
  ) => {
    if(confirmToDup) {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      setSubmittingDuplicationForm(true);

      axios.post(`/teams/${team.id}/confirms/${confirm.id}/duplicate`, {
        confirm: {
          title: confirmToDup.title,
          venue_id: confirmToDup.venue.id,
          new_room_ids: selectedRooms.map(room => room.value),
          promoter_id: confirmToDup.promoter?.value,
          buyer_id: confirmToDup.buyer?.value,
          start_time: offsetDateForServer(confirmToDup.start_time),
          end_time: offsetDateForServer(confirmToDup.end_time),
          door_time: offsetDateForServer(confirmToDup.door_time),
          ticket_forecast: confirmToDup.ticket_forecast,
          copy_offer_ids: confirmToDup.copy_offer_ids || [],
          note_attributes: {
            body: confirmToDup.note_state ? richTextToHtml(confirmToDup.note_state.getCurrentContent()) : null
          }
        }
      })
      .then(response => {
        window.location.href = `/teams/${team.id}/confirms/${response.data.id}`
      })
      .catch(errors => {
        setConfirmToDup({
          ...confirmToDup,
          errors: errors.response.data
        })
      })
      .finally(() => setSubmittingDuplicationForm(false))
    }
  }

  var isAttractionsMode = team.mode === "ATTRACTIONS_MANAGER";
  const showSettlements = team.package.pipeline && getConfirmPageSection(confirmPageSections, "settlements") && !isAttractionsMode && (offersWithSettlements(offers, v3Offers).length > 0 || (v3Offers || []).length > 0);

  var canShowManageOffersSection = (
    team.package.pipeline
      && getConfirmPageSection(confirmPageSections, "offers")
      && !isAttractionsMode
  );

  var canShowManageRentalInvoicesSection = (
    team.package.pipeline
      && getConfirmPageSection(confirmPageSections, "rental_invoices")
  );

  const sectionVisible = {
    lineup: Boolean(getConfirmPageSection(confirmPageSections, "lineup")) && !isAttractionsMode,
    schedule: confirm.type === "Confirm" && Boolean(getConfirmPageSection(confirmPageSections, "show_details")),
    invoices: Boolean(confirm.is_rental && canShowManageRentalInvoicesSection),
    offers: Boolean(canShowManageOffersSection),
    settlements: Boolean(showSettlements),
    finances: Boolean(team.package.pipeline && getConfirmPageSection(confirmPageSections, "finance_items")),
    notes: Boolean(getConfirmPageSection(confirmPageSections, "notes") && currentUser.can_read_notes),
    marketing: Boolean(confirm.type === "Confirm" && team.package.marketing && getConfirmPageSection(confirmPageSections, "marketing") && currentUser.can_read_posts),
    assets: Boolean(team.package.assets && getConfirmPageSection(confirmPageSections, "assets")),
    tickets: Boolean(confirm.type === "Confirm" && team.package.ticketing && getConfirmPageSection(confirmPageSections, "tickets")),
    pl: Boolean(getConfirmPageSection(confirmPageSections, "profit_loss") && team.package.pipeline && currentUser.role?.name === "Administrator"),
    links: Boolean(confirm.type === "Confirm" && getConfirmPageSection(confirmPageSections, "links") && canSeeAnyLink(confirm, currentUser)),
    contacts: Boolean(getConfirmPageSection(confirmPageSections, "contacts")),
  };

  const isPastShow = confirm.type === "Confirm" && confirm.calendar_classification === "past";
  const pastDashboard = usePastEventDashboard(team.id, confirm.id, isPastShow);
  const pastRailLast = ["tickets", "contacts"];
  const orderedSections = isPastShow
    ? [...SECTIONS.filter((section) => !pastRailLast.includes(section.key)), ...SECTIONS.filter((section) => pastRailLast.includes(section.key))]
    : SECTIONS;

  const navSections = orderedSections
    .filter((section) => sectionVisible[section.key])
    .map((section) => ({
      id: `sec-${section.key}`,
      label: section.key === "schedule" && isAttractionsMode ? "Event Details" : section.label,
      locked: Boolean(getConfirmPageSection(confirmPageSections, section.pageSection)?.permission_set_id),
    }));

  const [editDetailsRequest, setEditDetailsRequest] = useState(0);

  const jumpToSection = (id) => {
    document.getElementById(id.startsWith("sec-") ? id : `sec-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openRecapTarget = (target) => {
    if (target === "tickets") {
      window.location.href = `/teams/${team.id}/confirms/${confirm.id}/dashboard`;
    } else {
      jumpToSection(target);
    }
  };

  const statusBadge = <EventStatusBadge classification={confirm.calendar_classification} />;

  var confirmInvitationsAndTeamMemberships = pendingConfirmInvitations
    .concat(guestTeamMemberships)
    .sort((a, b) => a.name_with_email.localeCompare(b.name_with_email));

  useEffect(() => {
    offersWithSettlements(offers, v3Offers)
      .forEach(offer => fetchOfferTotalDue(team, offer));

    if(canShowManageOffersSection){
      fetchPossibleMatchingOffers(csrfToken, team, confirm);
    }
  }, [])

  useEffect(() => {
    applyTimeDefaults(
      holdToConfirm,
      doorsTimeDefault,
      eventEndTimeDefault,
      eventTimeDefaults,
      !userChangedDoorsTime,
      !userChangedEventEndTime,
      (holdToConfirm) => {
        holdToConfirmChanged(holdToConfirm);
      }
    );
  }, [holdToConfirm.start_time])

  useEffect(() => {
    if(confirmToDup?.start_time) {
      applyTimeDefaults(
        confirmToDup,
        doorsTimeDefault,
        eventEndTimeDefault,
        eventTimeDefaults,
        !userChangedDupDoorTime,
        !userChangedDupEndTime,
        (updatedConfirmToDup) => {
          setConfirmToDup(updatedConfirmToDup);
        }
      );
    }
  }, [confirmToDup?.start_time])

  return (
    <div className="confirm-show-page classic-event-page">
      <ToastContainer icon={false} />
      <div className="classic-header">
        <EventHeader
          title={confirm.name}
          rootCrumb={isGuest ? { label: "My Events", href: "/my_events" } : { label: "Calendar", href: "/dashboard" }}
          badges={
            <>
              {statusBadge}
              {confirm.is_rental ? <Badge color="orange" size="md">Rental</Badge> : null}
              <DemandHotPill />
            </>
          }
          meta={classicHeaderMeta(confirm, team)}
          edited={currentUser.can_menu_calendar_event ? (
            <LastEditIndicator ago={confirm.last_version_created_at_ago_in_words}
                               time={confirm.human_last_version_created_at}
                               versions={confirm.serialized_versions} />
          ) : null}
          actions={currentUser.can_menu_calendar_event ? (
            <>
              {teamMembershipId ? (
                <PageLayoutSelector
                  layout="classic"
                  teamId={team.id}
                  confirmId={confirm.id}
                  csrfToken={csrfToken}
                />
              ) : null}
              {currentUser.can_update_calendar_event && confirm.id ? (
                <Button variant="secondary" icon={Edit01} onClick={() => setEditDetailsRequest((n) => n + 1)}>
                  Edit details
                </Button>
              ) : null}
              {confirm.type === "Confirm" ? (
                <ConfirmActionMenu confirm={confirm}
                                csrfToken={csrfToken}
                                team={team}
                                currentUser={currentUser}
                                deleteConfirm={deleteConfirm}
                                cancelConfirm={cancelConfirm}
                                showAdvanceSendChanged={showAdvanceSendChanged}
                                confirmInvitationChanged={confirmInvitationChanged}
                                uncancelConfirm={uncancelConfirm}
                                postponeConfirm={postponeConfirm}
                                unpostponeConfirm={unpostponeConfirm}
                                mayCancelConfirm={mayCancelConfirm}
                                mayUncancelConfirm={mayUncancelConfirm}
                                mayUnpastConfirm={mayUnpastConfirm}
                                unpastTimeZone={unpastTimeZone}
                                unpastBlockers={unpastBlockers}
                                postEventSideEffects={postEventSideEffects}
                                setConfirmToDup={setConfirmToDup}
                                setMakeRecurringOpen={setMakeRecurringOpen}
                                selectedPromoter={selectedPromoter}
                                selectedBuyer={selectedBuyer}
                                showWidgetCodeChanged={showWidgetCodeChanged} />
              ) : (
                <HoldActionMenu confirm={confirm}
                                csrfToken={csrfToken}
                                team={team}
                                holdGroupDeletion={holdGroupDeletion}
                                holdGroupDeletionChanged={holdGroupDeletionChanged}
                                deleteHoldGroup={deleteHoldGroup}
                                holdToConfirm={holdToConfirm}
                                holdToConfirmChanged={holdToConfirmChanged}
                                userChangedDoorsTimeChanged={userChangedDoorsTimeChanged}
                                userChangedEventEndTimeChanged={userChangedEventEndTimeChanged}
                                convertHoldGroupToConfirm={convertHoldGroupToConfirm}
                                isConvertingHoldToConfirm={isConvertingHoldToConfirm}
                                venueOwnerships={venueOwnerships}
                                rooms={rooms}
                                eventTimeDefaults={eventTimeDefaults}
                                currentUser={currentUser} />
              )}
            </>
          ) : null}
        >
          <EventDetailsApp
            variant="classic"
            editRequest={editDetailsRequest}
            csrfToken={csrfToken}
            team={team}
            currentUser={currentUser}
            confirm={{...confirm}}
            confirmSaved={savedConfirm => confirmChanged(savedConfirm)}
            buyers={[...buyers]}
            promoters={promoters}
            financeItemsChanged={financeItemsChanged}
            rooms={rooms}
            venueOwnerships={venueOwnerships}
            isGuest={isGuest} />
        </EventHeader>
        {(confirm.event_series || []).map(series => (
          <EventSeriesBanner key={series.id} series={series} teamId={team.id} csrfToken={csrfToken} calendarEventId={confirm.id} startsAt={confirm.start_time} />
        ))}
        {currentUser.can_manage_tagged_items ? (
          <div style={{ display: 'none' }}>
            <TagManagerApp
              csrfToken={csrfToken}
              team={team}
              currentUser={currentUser}
              scope="CalendarEvent"
              title={`Tags for Event`}
              taggable={confirm}
              tagsButtonPosition="left"
              activeTagsAlignment="left"
              popupClasses="px-0 col-12 col-md-4 col-lg-6 position-absolute shadow"
            />
          </div>
        ) : null}
        {dayOfShowBar ? (
          <div className="event-page">
            <DayOfShowBar
              teamId={team.id}
              confirmId={confirm.id}
              {...dayOfShowBar}
              onSelectTab={showSettlements ? () => jumpToSection("settlements") : undefined}
            />
          </div>
        ) : null}
      </div>
      <ClassicSectionNav title={confirm.name}
                         status={statusBadge}
                         sections={navSections}
                         onJump={jumpToSection} />
      <div className="classic-columns">
        <div className="classic-columns__main">
          {pastDashboard ? (
            <div className="event-page classic-past-recap">
              <ShowRecapCard dashboard={pastDashboard} onSelectTab={openRecapTarget} />
              <SalesPaceCard salesSeries={pastDashboard.salesSeries} startTime={confirm.start_time} />
              <TicketTypesCard ticketBreakdown={pastDashboard.ticketBreakdown}
                               ticketsSold={pastDashboard.ticketsSold}
                               capacity={pastDashboard.capacity}
                               onSelectTab={openRecapTarget} />
            </div>
          ) : null}
          {sectionVisible.lineup ? (
            <ClassicSectionCard id="sec-lineup">
              <ErrorBoundary FallbackComponent={SectionErrorFallback}>
              <ManageLineup performers={performers}
                            currentUser={currentUser}
                            deletePerformer={deletePerformer}
                            csrfToken={csrfToken}
                            team={team}
                            stagePerformerForUpdate={stagePerformerForUpdate}
                            updatePerformer={updatePerformer}
                            updatePerformerActType={updatePerformerActType}
                            performerStartTimeChanged={performerStartTimeChanged}
                            performerEndTimeChanged={performerEndTimeChanged}
                            addNewPerformer={addNewPerformer}
                            setArtistOnNewPerformer={setArtistOnNewPerformer}
                            createPerformer={createPerformer}
                            cancelAddPerformer={cancelAddPerformer}
                            originalPerformer={originalPerformer}
                            cancelEditPerformer={cancelEditPerformer}
                            confirmPageSection={
                              getConfirmPageSection(confirmPageSections, "lineup")
                            }
                            fetchPermissionSet={fetchPermissionSet}
                            updateConfirmPageSection={updateConfirmPageSection}
                            confirm={confirm} />
              </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
          {sectionVisible.schedule ? (
            <ClassicSectionCard id="sec-schedule">
              <ErrorBoundary FallbackComponent={SectionErrorFallback}>
              <ManageShowActivities csrfToken={csrfToken}
                                    currentUser={currentUser}
                                    confirm={confirm}
                                    fetchConfirmShowActivities={fetchConfirmShowActivities}
                                    showActivities={showActivities}
                                    deleteShowActivity={deleteShowActivity}
                                    editShowActivity={editShowActivity}
                                    cancelEditShowActivity={cancelEditShowActivity}
                                    addNewShowActivity={addNewShowActivity}
                                    showActivityNameChanged={showActivityNameChanged}
                                    showActivityStartDateChanged={showActivityStartDateChanged}
                                    showActivityStartTimeChanged={showActivityStartTimeChanged}
                                    showActivityCopy={showActivityCopy}
                                    createShowActivity={createShowActivity}
                                    updateShowActivity={updateShowActivity}
                                    showAdvanceSends={showAdvanceSends}
                                    venuePreSalePromoCode={venuePreSalePromoCode}
                                    confirmPageSection={
                                      getConfirmPageSection(confirmPageSections, "show_details")
                                    }
                                    fetchPermissionSet={fetchPermissionSet}
                                    updateConfirmPageSection={updateConfirmPageSection}
                                    team={team} />
              </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
          {sectionVisible.invoices ? (
            <ClassicSectionCard id="sec-invoices">
            <ErrorBoundary FallbackComponent={SectionErrorFallback}>
            <ManageRentalInvoices
              csrfToken={csrfToken}
              team={team}
              confirm={confirm}
              currentUser={currentUser}
              rentalInvoices={rentalInvoices}
              confirmPageSection={
                getConfirmPageSection(confirmPageSections, "rental_invoices")
              }
              fetchPermissionSet={fetchPermissionSet}
              updateConfirmPageSection={updateConfirmPageSection}
            />
            </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
          {sectionVisible.offers ? (
            <ClassicSectionCard id="sec-offers">
              <ErrorBoundary FallbackComponent={SectionErrorFallback}>
              <ManageOffers csrfToken={csrfToken}
                            currentUser={currentUser}
                            confirm={confirm}
                            modalOffer={modalOffer}
                            defaultOffer={defaultOffer}
                            offerModalIsOpen={offerModalIsOpen}
                            closeOfferModal={closeOfferModal}
                            openOfferModal={openOfferModal}
                            offerTemplates={offerTemplates}
                            venuesOptions={venuesOptions}
                            onManageLineupInit={onManageLineupInit}
                            fetchOfferData={fetchOfferData}
                            refreshPerformer={refreshPerformer}
                            offerPerformer={offerPerformer}
                            setOfferPerformer={setOfferPerformer}
                            acceptOffer={acceptOffer}
                            declineOffer={declineOffer}
                            offers={offers}
                            v3Offers={v3Offers || []}
                            offerChanged={offerChanged}
                            updateOfferArtist={updateOfferArtist}
                            venueOwnerships={venueOwnerships}
                            refreshConfirm={refreshConfirm}
                            refreshConfirmOffers={refreshConfirmOffers}
                            refreshConfirmV3Offers={refreshConfirmV3Offers}
                            deleteOffer={deleteOffer}
                            venueSelected={venueSelected}
                            performers={performers}
                            offerForSettlement={offerForSettlement}
                            offerForSettlementChanged={offerForSettlementChanged}
                            confirmPageSection={
                              getConfirmPageSection(confirmPageSections, "offers")
                            }
                            fetchPermissionSet={fetchPermissionSet}
                            updateConfirmPageSection={updateConfirmPageSection}
                            possibleMatchingOffers={possibleMatchingOffers}
                            dismissMatchingOffers={dismissMatchingOffers}
                            acceptMatchingOffer={acceptMatchingOffer}
                            offerV3={offerV3}
                            acceptV3Offer={acceptV3Offer}
                            declineV3Offer={declineV3Offer}
                            deleteV3Offer={deleteV3Offer}
                            onV3OffersChanged={() => Promise.all([
                              sectionVisible.pl && fetchProfitAndLoss(csrfToken, team, confirm),
                              sectionVisible.finances && currentUser.can_manage_finance_items && refreshV3Deposits(team, confirm),
                            ])}
                            rooms={rooms}
                            team={team} />
              </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
          {sectionVisible.settlements ? (
            <ClassicSectionCard id="sec-settlements">
              <ErrorBoundary FallbackComponent={SectionErrorFallback}>
              <div className="card-body">
              <ManageSettlements csrfToken={csrfToken}
                                currentUser={currentUser}
                                confirm={confirm}
                                offerChanged={offerChanged}
                                v3OfferChanged={v3OfferChanged}
                                fetchOfferTotalDue={fetchOfferTotalDue}
                                offers={offersWithSettlements(offers, v3Offers)}
                                v3Offers={v3Offers || []}
                                offerForSettlement={offerForSettlement}
                                offerForSettlementChanged={offerForSettlementChanged}
                                confirmPageSection={
                                  getConfirmPageSection(confirmPageSections, "settlements")
                                }
                                fetchPermissionSet={fetchPermissionSet}
                                updateConfirmPageSection={updateConfirmPageSection}
                                team={team} />
              </div>
              </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
          {sectionVisible.finances ? (
            <ClassicSectionCard id="sec-finances">
              <ErrorBoundary FallbackComponent={SectionErrorFallback}>
              <ManageFinanceItems
                csrfToken={csrfToken}
                currentUser={currentUser}
                team={team}
                confirm={confirm}
                rentalDeposits={rentalDeposits}
                v3Deposits={v3Deposits}
                updateV3Deposit={updateV3Deposit}
                performers={performers}
                financeItems={financeItems}
                financeItemsChanged={financeItemsChanged}
                fetchNewFinanceItem={fetchNewFinanceItem}
                deleteFinanceItem={deleteFinanceItem}
                financeItemToEdit={financeItemToEdit}
                financeItemToEditChanged={financeItemToEditChanged}
                financeItemToEditCanceled={financeItemToEditCanceled}
                financeItemForForm={financeItemForForm}
                financeItemForFormChanged={financeItemForFormChanged}
                financeCategories={financeCategories}
                financeCategoriesChanged={financeCategoriesChanged}
                createFinanceItem={createFinanceItem}
                updateFinanceItem={updateFinanceItem}
                offers={offersWithSettlements(offers, v3Offers)}
                offerChanged={offerChanged}
                updateDeposit={updateDeposit}
                confirmPageSection={
                  getConfirmPageSection(confirmPageSections, "finance_items")
                }
                fetchPermissionSet={fetchPermissionSet}
                updateConfirmPageSection={updateConfirmPageSection}
              />
              </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
          {sectionVisible.notes ? (
            <ClassicSectionCard id="sec-notes">
              <ErrorBoundary FallbackComponent={SectionErrorFallback}>
              <div className="card-body">
              <Notes
                team={team}
                csrfToken={csrfToken}
                confirm={confirm}
                currentUser={currentUser}
                initialData={{
                  notes,
                  noteTemplates,
                  notesPageSection: getConfirmPageSection(confirmPageSections, "notes"),
                }}
                canReadNotes={currentUser.can_read_notes}
                canManageNotes={currentUser.can_manage_notes}
                canManageNoteTemplates={currentUser.can_manage_note_templates}
                canManagePermissions={currentUser.role && currentUser.role.name === "Administrator"}
                guestTeamMemberships={guestTeamMemberships}
                promotersWithPermission={promotersWithPermission}
                onNotesChanged={notesReplaced}
              />
              </div>
              </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
          {sectionVisible.marketing ? (
            <ClassicSectionCard id="sec-marketing">
              <ErrorBoundary FallbackComponent={SectionErrorFallback}>
              <MarketingSection csrfToken={csrfToken}
                                team={team}
                                currentUser={currentUser}
                                sendingCustomerIOEmail={sendingCustomerIOEmail}
                                sendingCustomerIOEmailChanged={sendingCustomerIOEmailChanged}
                                customerIOEmailPurpose={customerIOEmailPurpose}
                                customerIOEmailPurposeChanged={customerIOEmailPurposeChanged}
                                customerIOEmailTemplate={customerIOEmailTemplate}
                                customerIOEmailTemplateChanged={customerIOEmailTemplateChanged}
                                createCustomerIOEmail={createCustomerIOEmail}
                                marketingSectionActiveTab={marketingSectionActiveTab}
                                marketingSectionActiveTabChanged={marketingSectionActiveTabChanged}
                                authorizedPages={authorizedPages}
                                addingNewSocialAccount={addingNewSocialAccount}
                                addingNewSocialAccountChanged={addingNewSocialAccountChanged}
                                onepostPublicKey={onepostPublicKey}
                                twitterProviderId={twitterProviderId}
                                facebookProviderId={facebookProviderId}
                                composingPost={composingPost}
                                composingPostChanged={composingPostChanged}
                                createPost={createPost}
                                deletePost={deletePost}
                                postToEdit={postToEdit}
                                postToEditChanged={postToEditChanged}
                                updatePost={updatePost}
                                posts={posts}
                                updateConfirmPageSection={updateConfirmPageSection}
                                fetchPermissionSet={fetchPermissionSet}
                                socialMarketingBudgetItems={socialMarketingBudgetItems}
                                traditionalMarketingBudgetItems={traditionalMarketingBudgetItems}
                                metaAdSpend={metaAdSpend}
                                metaAdsConnected={metaAdsConnected}
                                addSocialMarketingBudgetItem={addSocialMarketingBudgetItem}
                                addTraditionalMarketingBudgetItem={addTraditionalMarketingBudgetItem}
                                createMarketingBudgetItem={createMarketingBudgetItem}
                                destroyMarketingBudgetItem={destroyMarketingBudgetItem}
                                editMarketingBudgetItem={editMarketingBudgetItem}
                                updateMarketingBudgetItem={updateMarketingBudgetItem}
                                marketingBudgetItemToEdit={marketingBudgetItemToEdit}
                                marketingBudgetItemToEditChanged={marketingBudgetItemToEditChanged}
                                confirmPageSection={
                                  getConfirmPageSection(confirmPageSections, "marketing")
                                }
                                confirm={confirm} />
              </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
          {sectionVisible.assets ? (
            <ClassicSectionCard id="sec-assets">
              <ErrorBoundary FallbackComponent={SectionErrorFallback}>
              <FolderViewApp csrfToken={csrfToken}
                              folder={folder}
                              teamRootFolder={teamRootFolder}
                              onFolderChanged={folderChanged}
                              currentUser={currentUser}
                              confirm={confirm}
                              guestTeamMemberships={guestTeamMemberships}
                              promotersWithPermission={promotersWithPermission}
                              headerComponent={
                                () => {
                                  const assetsPageSection = getConfirmPageSection(confirmPageSections, "assets");
                                  return (
                                    <h5 className="mb-0 strong">
                                      Assets
                                      {assetsPageSection.permission_set_id ? (
                                        <i className="ml-2 far fa-lock small text-danger"></i>
                                      ) : null}
                                    </h5>
                                  );
                                }
                              }
                              menuComponent={
                                () => {
                                  const assetsPageSection = getConfirmPageSection(confirmPageSections, "assets");
                                  return (
                                    <PageSectionMenu csrfToken={csrfToken}
                                                     team={team}
                                                     confirm={confirm}
                                                     currentUser={currentUser}
                                                     confirmPageSection={assetsPageSection}
                                                     fetchPermissionSet={fetchPermissionSet}
                                                     updateConfirmPageSection={updateConfirmPageSection} />
                                  );
                                }
                              }
                              team={team} />
              </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
        </div>
        <div className={`classic-columns__rail${isPastShow ? " classic-columns__rail--past" : ""}`}>
          {pastDashboard?.settlement ? (
            <div className="event-page">
              <SettlementCard settlement={pastDashboard.settlement} />
            </div>
          ) : null}
          {sectionVisible.tickets ? (
            <ClassicSectionCard id="sec-tickets">
              <ErrorBoundary FallbackComponent={SectionErrorFallback}>
              {!confirm.eventbrite_event_id && currentUser.can_access_opendate_ticketing ? (
                <ManageOpendateTickets csrfToken={csrfToken}
                                       confirm={confirm}
                                       confirmChanged={confirmChanged}
                                       updateConfirmTicketForecast={updateConfirmTicketForecast}
                                       currentUser={currentUser}
                                       ticketTypesByPrice={ticketTypesByPrice}
                                       addOnsByPrice={addOnsByPrice}
                                       showWidgetCodeChanged={showWidgetCodeChanged}
                                       manualTicketCounts={manualTicketCounts}
                                       confirmPageSection={
                                         getConfirmPageSection(confirmPageSections, "tickets")
                                       }
                                       fetchPermissionSet={fetchPermissionSet}
                                       updateConfirmPageSection={updateConfirmPageSection}
                                       team={team} />
              ) : (
                <ManageTickets csrfToken={csrfToken}
                               confirm={confirm}
                               currentUser={currentUser}
                               confirmPageSection={
                                 getConfirmPageSection(confirmPageSections, "tickets")
                               }
                               fetchPermissionSet={fetchPermissionSet}
                               updateConfirmPageSection={updateConfirmPageSection}
                               team={team} />
              )}
              </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
          {sectionVisible.pl ? (
            <ClassicSectionCard id="sec-pl">
              <ErrorBoundary FallbackComponent={SectionErrorFallback}>
              <ProfitAndLoss
                csrfToken={csrfToken}
                currentUser={currentUser}
                team={team}
                confirm={confirm}
                profitAndLoss={profitAndLoss}
                fetchProfitAndLoss={fetchProfitAndLoss}
                confirmPageSection={
                  getConfirmPageSection(confirmPageSections, "profit_loss")
                }
                fetchPermissionSet={fetchPermissionSet}
                updateConfirmPageSection={updateConfirmPageSection}
              />
              </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
          {sectionVisible.links ? (
            <ClassicSectionCard id="sec-links">
              <ErrorBoundary FallbackComponent={SectionErrorFallback}>
              <CopyLinks csrfToken={csrfToken}
                         confirm={confirm}
                         confirmChanged={confirmChanged}
                         updateConfirmWebsite={updateConfirmWebsite}
                         updateConfirmWebsiteRedirect={updateConfirmWebsiteRedirect}
                         currentUser={currentUser}
                         selfRegisterLink={selfRegisterLink}
                         team={team}
                         showUpgradeLink={showUpgradeLink}
                         showDonationLink={showDonationLink}
                         editingConfirmWebsite={editingConfirmWebsite}
                         editingConfirmWebsiteChanged={editingConfirmWebsiteChanged}
                         editingConfirmWebsiteValue={editingConfirmWebsiteValue}
                         editingConfirmWebsiteValueChanged={editingConfirmWebsiteValueChanged}
                         confirmPageSection={
                           getConfirmPageSection(confirmPageSections, "links")
                         }
                         fetchPermissionSet={fetchPermissionSet}
                         updateConfirmPageSection={updateConfirmPageSection} />
              </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
          {sectionVisible.contacts ? (
            <ClassicSectionCard id="sec-contacts">
              <ErrorBoundary FallbackComponent={SectionErrorFallback}>
              <ManageContacts csrfToken={csrfToken}
                              currentUser={currentUser}
                              confirm={confirm}
                              calendarEventContacts={calendarEventContacts}
                              createCalendarEventContactFromContactId={createCalendarEventContactFromContactId}
                              deleteCalendarEventContact={deleteCalendarEventContact}
                              addCalendarEventContact={addCalendarEventContact}
                              removeUnsavedCalendarEventContact={removeUnsavedCalendarEventContact}
                              editCalendarEventContact={editCalendarEventContact}
                              cancelEditCalendarEventContact={cancelEditCalendarEventContact}
                              updateCalendarEventContactName={updateCalendarEventContactName}
                              updateCalendarEventContactEmail={updateCalendarEventContactEmail}
                              updateCalendarEventContactPhone={updateCalendarEventContactPhone}
                              updateCalendarEventContactContactType={updateCalendarEventContactContactType}
                              updateCalendarEventContactTitle={updateCalendarEventContactTitle}
                              updateCalendarEventContactCompanyName={updateCalendarEventContactCompanyName}
                              submitCalendarEventContactForm={submitCalendarEventContactForm}
                              editUnsavedCalendarEventContact={editUnsavedCalendarEventContact}
                              originalCalendarEventContact={originalCalendarEventContact}
                              createCalendarEventContactFromAgentId={createCalendarEventContactFromAgentId}
                              updateCalendarEventContact={updateCalendarEventContact}
                              sendCalendarEventContactTicketCounts={sendCalendarEventContactTicketCounts}
                              confirmInvitationChanged={confirmInvitationChanged}
                              emailsToInviteChanged={emailsToInviteChanged}
                              confirmInvitationsAndTeamMemberships={confirmInvitationsAndTeamMemberships}
                              confirmPageSection={
                                getConfirmPageSection(confirmPageSections, "contacts")
                              }
                              fetchPermissionSet={fetchPermissionSet}
                              updateConfirmPageSection={updateConfirmPageSection}
                              team={team} />
              </ErrorBoundary>
            </ClassicSectionCard>
          ) : null}
        </div>
      </div>
      <ShareSettingsPopup csrfToken={csrfToken}
                          team={team}
                          confirm={confirm}
                          confirmInvitation={confirmInvitation}
                          confirmInvitationChanged={confirmInvitationChanged}
                          emailsToInvite={emailsToInvite}
                          emailsToInviteChanged={emailsToInviteChanged}
                          guestRole={guestRole}
                          createConfirmInvitations={createConfirmInvitations}
                          confirmInvitationsAndTeamMemberships={confirmInvitationsAndTeamMemberships}
                          confirmInvitationMemoChanged={confirmInvitationMemoChanged}
                          calendarEventContacts={calendarEventContacts}
                          isViewingGuests={isViewingGuests}
                          isViewingGuestsChanged={isViewingGuestsChanged}
                          deleteConfirmInvitation={deleteConfirmInvitation}
                          removeConfirmAccess={removeConfirmAccess}
                          shareSettingsPopupClosed={shareSettingsPopupClosed}
                          currentUser={currentUser} />
      <SetPermissionsPopup csrfToken={csrfToken}
                           team={team}
                           confirm={confirm}
                           permissionSet={permissionSet}
                           permissionSetChanged={permissionSetChanged}
                           permissionRuleEnabledClicked={permissionRuleEnabledClicked}
                           selectAllPermissionRulesClicked={selectAllPermissionRulesClicked}
                           submitPermissionSet={submitPermissionSet}
                           onPermissionSetSaved={onPermissionSetSaved}
                           guestTeamMemberships={guestTeamMemberships}
                           promotersWithPermission={promotersWithPermission}
                           currentUser={currentUser} />
      <ShowAdvanceSendPopup key={Object.keys(showAdvanceSend).length > 0 ? "sa-open" : "sa-closed"}
                            initialMode="email"
                            csrfToken={csrfToken}
                            confirm={confirm}
                            currentUser={currentUser}
                            showAdvanceSend={showAdvanceSend}
                            showAdvanceSendChanged={showAdvanceSendChanged}
                            showAdvanceSendToChanged={showAdvanceSendToChanged}
                            showAdvanceSendSubjectChanged={showAdvanceSendSubjectChanged}
                            showAdvanceSendBodyChanged={showAdvanceSendBodyChanged}
                            createShowAdvanceSend={createShowAdvanceSend}
                            calendarEventContacts={calendarEventContacts}
                            showAdvanceSendNoteClicked={showAdvanceSendNoteClicked}
                            toggleHideLineupFromAdvance={toggleHideLineupFromAdvance}
                            toggleHideShowDetailsFromAdvance={toggleHideShowDetailsFromAdvance}
                            notes={notes}
                            team={team} />
      {showWidgetCode ? (
        <CheckoutEmbedModal
          open
          onClose={() => showWidgetCodeChanged(false)}
          iframeId={`od-confirm-${confirm.id}-iframe`}
          title={confirm.title}
          embedUrl={confirm.widget_embed_url} />
      ) : null}
      {user.can_create_calendar_event ? (
        <Popup
          open={confirmToDup !== null}
          arrow={false}
          position="center center"
          contentStyle={newContentStyle(browser)}
          overlayStyle={{ zIndex: "199" }}
          onClose={() => setConfirmToDup(null)}
          lockScroll={true}
        >
          {close => (
            <div
              className="text-left"
              style={{"overflowY": "auto", "overflowX": "hidden", "maxHeight": "96vh"}}
            >
              {confirmToDup ? (
                <EventForm
                  forDuplication={true}
                  formTitle={"Duplicate Event"}
                  formButtonLabel={"Duplicate"}
                  csrfToken={csrfToken}
                  currentUser={user}
                  team={team}
                  venuesOptions={venuesOptions}
                  venueSelected={confirmToDup.venue}
                  venueOwnerships={venueOwnerships}
                  rooms={rooms}
                  submitEventForm={submitDuplicationForm}
                  onSuccess={url => { window.location.href = url; }}
                  updateSelectedVenue={(options, selected) => {
                    setConfirmToDup({...confirmToDup, venue: options.find(venue => venue.id === selected?.value)})
                  }}
                  onClose={() => close()}
                  confirm={confirmToDup}
                  confirmChanged={setConfirmToDup}
                  calendarEventFormClosed={(func) => func()}
                  calendarEventTitle={confirmToDup.title}
                  updateCalendarEventTitle={(title) => setConfirmToDup({...confirmToDup, title: title})}
                  calendarEventType="Confirm"
                  updateCalendarEventType={() => {}}
                  calendarEventTicketForecast={confirmToDup.ticket_forecast}
                  calendarEventTicketForecastChanged={(forecast) => setConfirmToDup({...confirmToDup, ticket_forecast: forecast})}
                  calendarEventNoteEditorState={confirmToDup.note_state}
                  updateCalendarEventNoteEditorState={(noteState) => setConfirmToDup({...confirmToDup, note_state: noteState})}
                  confirmStartDateUpdated={() => {}}
                  confirmStartTimeUpdated={() => {}}
                  userChangedCalendarEventTitle={true}
                  promoters={promoters}
                  promotersChanged={promotersChanged}
                  selectedPromoter={confirmToDup.promoter}
                  selectedPromoterChanged={promoter => setConfirmToDup({...confirmToDup, promoter: promoter})}
                  buyers={buyers}
                  buyersChanged={buyersChanged}
                  selectedBuyer={confirmToDup.buyer}
                  selectedBuyerChanged={buyer => setConfirmToDup({...confirmToDup, buyer: buyer})}
                  submittingForm={submittingDuplicationForm}
                  calendarEventErrors={confirmToDup?.errors || []}
                  doorsTimeDefault={doorsTimeDefault}
                  eventEndTimeDefault={eventEndTimeDefault}
                  eventTimeDefaults={eventTimeDefaults}
                  userChangedDoorsTime={userChangedDupDoorTime}
                  userChangedDoorsTimeChanged={setUserChangedDupDoorTime}
                  userChangedEventEndTime={userChangedDupEndTime}
                  userChangedEventEndTimeChanged={setUserChangedDupEndTime}
                  applyTimeDefaults={applyTimeDefaults}
                />
              ) : null}
            </div>
          )}
        </Popup>
      ) : null}
      <MakeRecurringModal
        open={makeRecurringOpen}
        confirm={confirm}
        team={team}
        onClose={() => setMakeRecurringOpen(false)}
        onCreated={(series) => { window.location = `/teams/${team.id}/event_series/${series.id}`; }}
      />
    </div>
  );
};

const ConfirmShow = (props) => (
  <EventDemandProvider confirm={props.confirm}>
    <ConfirmShowBody {...props} />
  </EventDemandProvider>
);

ConfirmShow.propTypes = {
  browser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  confirmChanged: PropTypes.func.isRequired,
  refreshConfirm: PropTypes.func.isRequired,
  performers: PropTypes.array,
  deletePerformer: PropTypes.func.isRequired,
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  stagePerformerForUpdate: PropTypes.func.isRequired,
  updatePerformer: PropTypes.func.isRequired,
  updatePerformerActType: PropTypes.func.isRequired,
  performerStartTimeChanged: PropTypes.func.isRequired,
  performerEndTimeChanged: PropTypes.func.isRequired,
  addNewPerformer: PropTypes.func.isRequired,
  setArtistOnNewPerformer: PropTypes.func.isRequired,
  createPerformer: PropTypes.func.isRequired,
  cancelAddPerformer: PropTypes.func.isRequired,
  originalPerformer: PropTypes.object,
  cancelEditPerformer: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  notes: PropTypes.array,
  notesReplaced: PropTypes.func,
  noteTemplates: PropTypes.array,
  calendarEventContacts: PropTypes.array,
  createCalendarEventContactFromContactId: PropTypes.func.isRequired,
  deleteCalendarEventContact: PropTypes.func.isRequired,
  addCalendarEventContact: PropTypes.func.isRequired,
  removeUnsavedCalendarEventContact: PropTypes.func.isRequired,
  editCalendarEventContact: PropTypes.func.isRequired,
  cancelEditCalendarEventContact: PropTypes.func.isRequired,
  updateCalendarEventContactName: PropTypes.func.isRequired,
  updateCalendarEventContactEmail: PropTypes.func.isRequired,
  updateCalendarEventContactPhone: PropTypes.func.isRequired,
  updateCalendarEventContactContactType: PropTypes.func.isRequired,
  updateCalendarEventContactTitle: PropTypes.func.isRequired,
  updateCalendarEventContactCompanyName: PropTypes.func.isRequired,
  submitCalendarEventContactForm: PropTypes.func.isRequired,
  editUnsavedCalendarEventContact: PropTypes.func.isRequired,
  originalCalendarEventContact: PropTypes.object,
  createCalendarEventContactFromAgentId: PropTypes.func.isRequired,
  modalOffer: PropTypes.object,
  defaultOffer: PropTypes.object,
  offerModalIsOpen: PropTypes.bool,
  closeOfferModal: PropTypes.func.isRequired,
  openOfferModal: PropTypes.func.isRequired,
  offerTemplates: PropTypes.array,
  venuesOptions: PropTypes.array,
  onManageLineupInit: PropTypes.func.isRequired,
  fetchOfferData: PropTypes.func.isRequired,
  refreshPerformer: PropTypes.func.isRequired,
  offerPerformer: PropTypes.object,
  setOfferPerformer: PropTypes.func.isRequired,
  acceptOffer: PropTypes.func.isRequired,
  declineOffer: PropTypes.func.isRequired,
  offers: PropTypes.array,
  refreshConfirmOffers: PropTypes.func.isRequired,
  refreshConfirmV3Offers: PropTypes.func.isRequired,
  deleteOffer: PropTypes.func.isRequired,
  folder: PropTypes.object.isRequired,
  deleteConfirm: PropTypes.func.isRequired,
  cancelConfirm: PropTypes.func.isRequired,
  sendingCustomerIOEmail: PropTypes.bool,
  sendingCustomerIOEmailChanged: PropTypes.func.isRequired,
  customerIOEmailPurpose: PropTypes.string,
  customerIOEmailPurposeChanged: PropTypes.func.isRequired,
  customerIOEmailTemplate: PropTypes.string,
  customerIOEmailTemplateChanged: PropTypes.func.isRequired,
  createCustomerIOEmail: PropTypes.func.isRequired,
  marketingSectionActiveTab: PropTypes.string,
  marketingSectionActiveTabChanged: PropTypes.func.isRequired,
  authorizedPages: PropTypes.array,
  addingNewSocialAccount: PropTypes.bool,
  addingNewSocialAccountChanged: PropTypes.func.isRequired,
  onepostPublicKey: PropTypes.string,
  twitterProviderId: PropTypes.number,
  facebookProviderId: PropTypes.number,
  composingPost: PropTypes.bool,
  composingPostChanged: PropTypes.func.isRequired,
  createPost: PropTypes.func.isRequired,
  posts: PropTypes.array,
  deletePost: PropTypes.func.isRequired,
  postToEdit: PropTypes.object,
  postToEditChanged: PropTypes.func.isRequired,
  updatePost: PropTypes.func.isRequired,
  updateCalendarEventContact: PropTypes.func.isRequired,
  sendCalendarEventContactTicketCounts: PropTypes.func.isRequired,
  fetchConfirmShowActivities: PropTypes.func.isRequired,
  showActivities: PropTypes.array,
  deleteShowActivity: PropTypes.func.isRequired,
  editShowActivity: PropTypes.func.isRequired,
  cancelEditShowActivity: PropTypes.func.isRequired,
  addNewShowActivity: PropTypes.func.isRequired,
  showActivityNameChanged: PropTypes.func.isRequired,
  showActivityStartDateChanged: PropTypes.func.isRequired,
  showActivityStartTimeChanged: PropTypes.func.isRequired,
  showActivityCopy: PropTypes.object,
  createShowActivity: PropTypes.func.isRequired,
  updateShowActivity: PropTypes.func.isRequired,
  showAdvanceSend: PropTypes.object,
  showAdvanceSendChanged: PropTypes.func.isRequired,
  showAdvanceSendToChanged: PropTypes.func.isRequired,
  showAdvanceSendSubjectChanged: PropTypes.func.isRequired,
  showAdvanceSendBodyChanged: PropTypes.func.isRequired,
  createShowAdvanceSend: PropTypes.func.isRequired,
  showAdvanceSendNoteClicked: PropTypes.func.isRequired,
  venueSelected: PropTypes.object,
  confirmInvitation: PropTypes.object,
  confirmInvitationChanged: PropTypes.func.isRequired,
  confirmInvitationMemoChanged: PropTypes.func.isRequired,
  emailsToInvite: PropTypes.array,
  emailsToInviteChanged: PropTypes.func.isRequired,
  guestRole: PropTypes.object.isRequired,
  createConfirmInvitations: PropTypes.func.isRequired,
  pendingConfirmInvitations: PropTypes.array,
  guestTeamMemberships: PropTypes.array,
  isViewingGuests: PropTypes.bool,
  isViewingGuestsChanged: PropTypes.func.isRequired,
  updateConfirmWebsite: PropTypes.func.isRequired,
  updateConfirmWebsiteRedirect: PropTypes.func.isRequired,
  updateConfirmTicketForecast: PropTypes.func.isRequired,
  deleteConfirmInvitation: PropTypes.func.isRequired,
  removeConfirmAccess: PropTypes.func.isRequired,
  shareSettingsPopupClosed: PropTypes.func.isRequired,
  selfRegisterLink: PropTypes.string,
  offerForSettlement: PropTypes.object,
  offerForSettlementChanged: PropTypes.func.isRequired,
  permissionSet: PropTypes.object,
  permissionSetChanged: PropTypes.func.isRequired,
  fetchPermissionSet: PropTypes.func.isRequired,
  permissionRuleEnabledClicked: PropTypes.func.isRequired,
  selectAllPermissionRulesClicked: PropTypes.func.isRequired,
  submitPermissionSet: PropTypes.func.isRequired,
  onPermissionSetSaved: PropTypes.func.isRequired,
  confirmPageSections: PropTypes.array,
  updateConfirmPageSection: PropTypes.func.isRequired,
  promotersWithPermission: PropTypes.array,
  showAdvanceSends: PropTypes.array,
  socialMarketingBudgetItems: PropTypes.array,
  traditionalMarketingBudgetItems: PropTypes.array,
  metaAdSpend: PropTypes.array,
  metaAdsConnected: PropTypes.bool,
  addSocialMarketingBudgetItem: PropTypes.func.isRequired,
  addTraditionalMarketingBudgetItem: PropTypes.func.isRequired,
  createMarketingBudgetItem: PropTypes.func.isRequired,
  destroyMarketingBudgetItem: PropTypes.func.isRequired,
  editMarketingBudgetItem: PropTypes.func.isRequired,
  updateMarketingBudgetItem: PropTypes.func.isRequired,
  marketingBudgetItemToEdit: PropTypes.object,
  marketingBudgetItemToEditChanged: PropTypes.func.isRequired,
  financeItems: PropTypes.array,
  financeItemsChanged: PropTypes.func.isRequired,
  financeCategories: PropTypes.array,
  financeCategoriesChanged: PropTypes.func.isRequired,
  fetchNewFinanceItem: PropTypes.func.isRequired,
  deleteFinanceItem: PropTypes.func.isRequired,
  financeItemToEdit: PropTypes.object,
  financeItemToEditChanged: PropTypes.func.isRequired,
  financeItemToEditCanceled: PropTypes.func.isRequired,
  financeItemForForm: PropTypes.object,
  financeItemForFormChanged: PropTypes.func.isRequired,
  createFinanceItem: PropTypes.func.isRequired,
  updateFinanceItem: PropTypes.func.isRequired,
  ticketTypesByPrice: PropTypes.array,
  showWidgetCode: PropTypes.bool,
  showWidgetCodeChanged: PropTypes.func.isRequired,
  addOnsByPrice: PropTypes.array,
  uncancelConfirm: PropTypes.func.isRequired,
  postponeConfirm: PropTypes.func.isRequired,
  unpostponeConfirm: PropTypes.func.isRequired,
  buyers: PropTypes.array,
  buyersChanged: PropTypes.func.isRequired,
  selectedBuyer: PropTypes.object,
  selectedBuyerChanged: PropTypes.func.isRequired,
  promoters: PropTypes.array,
  promotersChanged: PropTypes.func.isRequired,
  selectedPromoter: PropTypes.object,
  selectedPromoterChanged: PropTypes.func.isRequired,
  venueOwnerships: PropTypes.array,
  editingConfirmWebsite: PropTypes.bool,
  editingConfirmWebsiteChanged: PropTypes.func.isRequired,
  editingConfirmWebsiteValue: PropTypes.string,
  editingConfirmWebsiteValueChanged: PropTypes.func.isRequired,
  fetchOfferTotalDue: PropTypes.func.isRequired,
  offerChanged: PropTypes.func.isRequired,
  updateOfferArtist: PropTypes.func.isRequired,
  folderChanged: PropTypes.func.isRequired,
  teamRootFolder: PropTypes.object.isRequired,
  profitAndLoss: PropTypes.object,
  profitAndLossChanged: PropTypes.func.isRequired,
  fetchProfitAndLoss: PropTypes.func.isRequired,
  refreshV3Deposits: PropTypes.func.isRequired,
  updateDeposit: PropTypes.func.isRequired,
  updateV3Deposit: PropTypes.func.isRequired,
  dayOfShowBar: PropTypes.object,
  mayCancelConfirm: PropTypes.bool,
  mayUncancelConfirm: PropTypes.bool,
  mayUnpastConfirm: PropTypes.bool,
  unpastTimeZone: PropTypes.string,
  unpastBlockers: PropTypes.arrayOf(PropTypes.string),
  postEventSideEffects: PropTypes.object,
  holdGroupDeletion: PropTypes.object,
  holdGroupDeletionChanged: PropTypes.func.isRequired,
  deleteHoldGroup: PropTypes.func.isRequired,
  holdToConfirm: PropTypes.object,
  holdToConfirmChanged: PropTypes.func.isRequired,
  applyTimeDefaults: PropTypes.func.isRequired,
  doorsTimeDefault: PropTypes.object.isRequired,
  eventEndTimeDefault: PropTypes.object.isRequired,
  userChangedDoorsTime: PropTypes.bool,
  userChangedDoorsTimeChanged: PropTypes.func.isRequired,
  userChangedEventEndTime: PropTypes.bool,
  userChangedEventEndTimeChanged: PropTypes.func.isRequired,
  convertHoldGroupToConfirm: PropTypes.func.isRequired,
  isConvertingHoldToConfirm: PropTypes.bool,
  possibleMatchingOffers: PropTypes.array,
  fetchPossibleMatchingOffers: PropTypes.func.isRequired,
  dismissMatchingOffers: PropTypes.func.isRequired,
  acceptMatchingOffer: PropTypes.func.isRequired,
  manualTicketCounts: PropTypes.array,
  showUpgradeLink: PropTypes.bool,
  showDonationLink: PropTypes.bool,
  venuePreSalePromoCode: PropTypes.object,
  rooms: PropTypes.array
};

export default ConfirmShow;
